// Angular and Ionic Core Imports
import { AfterViewInit, Component, DestroyRef, inject, OnInit } from "@angular/core";
// takeUntilDestroyed is used in constructor (languageEmit$ pipeline + selectedlanguage$ subscription)
// and in ngAfterViewInit (userDetails$ subscription) to auto-unsubscribe on component destroy.
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { AlertController, IonicModule } from "@ionic/angular";

// Third-Party Services and Pipes
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, from, interval, Observable, of, Subject, Subscription, timer } from "rxjs";
import { catchError, filter, map, switchMap, take, tap } from "rxjs/operators";

// App Animations and Constants
import { bounceIn } from "src/app/animations/custom.animation";
import { CommonVariable } from "src/app/app.constants";
import { Language } from "@interfaces/translation-interface";

// Services
import { AuthService } from "@core/auth.service";
import { AuthPartialRegService } from "@core/AuthServices/authPartialReg/auth-partial-reg.service";
import { LoadingService } from "@core/loading.service";
import { PaymentDataService } from "src/app/services/payment-data.service";
import { PaymentService } from "src/app/services/payment.service";


// Angular Material Components
import { NgClass, NgIf } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { SafeHtmlPipe } from "src/app/pipe/safehtmlpipe";
import { NoThanksPopupComponent } from "../../sales/no-thanks-popup/no-thanks-popup.component";
@Component({
  selector: "app-free-reg-step4j",
  templateUrl: "./free-reg-step4j.component.html",
  styleUrl: "./free-reg-step4j.component.scss",
  animations: [bounceIn],
  standalone: true,
  imports: [
    IonicModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgClass,
    MatProgressSpinner,
    NgIf,
    SafeHtmlPipe,
  ],
})
export class FreeRegStep4jComponent implements OnInit, AfterViewInit {
  // Form & State Variables

  accountDetails: FormGroup;
  addressList: Data[] = []; // Stores fetched addresses from postcode lookup
  profilePicState$ = new BehaviorSubject("noProfilePic");
  progressValue = 5;
  priceValue: number;
  formValidation: boolean;
  private matDialog = inject(MatDialog);
  // Language & Currency
  selectedLanguage: string = CommonVariable?.preferredLanguage;
  preferredCurrency: string = CommonVariable?.preferredCurrency;
  progressBar: number;
  timer$: Data;
  sub: Subscription;
  // Route Data
  routerData: Data;
  spinnerAnimation: boolean;
  navParams: Data;
  offerName: string;
  proceed = true;
  optTemplateHtml: string;


  getFieldError(field: string): string | null {
    const control = this.accountDetails.get(field);
    if (!control || !(control.dirty || control.touched) || !control.errors) return null;

    const errors = control.errors;

    const messages: { [key: string]: { [errorType: string]: string } } = {
      cardholder_name: {
        required: 'Cardholder name is required.',
        pattern: 'Cardholder  name not valid.'
      },
      account_number: {
        required: 'Account number is required.',
        pattern: 'Account number not valid.'
      },
      sort_code: {
        required: 'Sort code is required.',
        pattern: 'Sort code not valid.'
      },
      post_code: {
        required: 'Post code is required.',
        pattern: 'Post code not valid.'
      },
      address_1: {
        required: 'Address Line 1 is required.'
      },
      address_2: {
        required: 'Address Line 2 is required.'
      },
      town: {
        required: 'Town is required.'
      }
    };

    const fieldErrors = messages[field];
    if (!fieldErrors) return null;

    const firstErrorKey = Object.keys(errors)[0];
    return fieldErrors[firstErrorKey] || null;
  }
  public currencySymbol = "£";
  private fb = inject(FormBuilder);
  private loadingService = inject(LoadingService);
  private paymentService = inject(PaymentService);
  private paymentDataService = inject(PaymentDataService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private authPartialRegService = inject(AuthPartialRegService);
  private actRoute = inject(ActivatedRoute);
  private alertController = inject(AlertController);
  private analytics = inject(AngularFireAnalytics);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  /**
   * Routes language-change requests through switchMap so that only the latest
   * call to initializeLanguage() wins if it is somehow invoked multiple times
   * before the deferred timer fires.  The timer(0) deferral is the key fix:
   * it prevents a synchronous selectedlanguage$.next() from firing mid-call-stack
   * on WebKit/iPhone (see initializeLanguage comments).
   */
  private readonly languageEmit$ = new Subject<string>();

  userId: string;
  constructor() {
    this.priceValue = 0;
    // Initialize the reactive form with validation rules
    this.accountDetails = this.fb.group({

      cardholder_name: ['', [Validators.required, Validators.pattern('^[a-zA-Z ]*$')]],
      account_number: ['', [Validators.required, Validators.maxLength(12), Validators.pattern('^[0-9]*$') , Validators.minLength(8)]],
      sort_code: ['', [Validators.required, this.sortCodeSixDigitValidator]],
      post_code: ['', [Validators.required, Validators.maxLength(10)]],
      address_1: ['', Validators.required],
      address_2: [''],
      town: ['', Validators.required],
    });

    // Wire up the deferred language emitter.
    // switchMap cancels any in-flight timer if a newer language value arrives,
    // so only the latest selectedlanguage$.next() call is emitted.
    // Note: this deferral applies only to the shared selectedlanguage$ emission;
    // translate.use() in initializeLanguage() still runs synchronously on every call.
    this.languageEmit$
      .pipe(
        switchMap((lang) => timer(0).pipe(map(() => lang))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((lang) => {
        this.authService.selectedlanguage$.next(lang);
      });



    // Subscribe to language preference BEFORE getActivatedRoute() so that any
    // synchronous code inside getActivatedRoute() that reads this.selectedLanguage
    // or localStorage.language_code observes the current value immediately.
    // Note: selectedlanguage$.next() from initializeLanguage() is still deferred
    // via languageEmit$ + timer(0), so this subscriber will also fire asynchronously
    // for that initial emission.  The immediate synchronous emission here is the
    // BehaviorSubject replaying its current value on subscription.
    this.authService.selectedlanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((l) => {
      // Normalize so empty/mixed-case/unsupported values from other emitters
      // cannot bypass normalizeLanguageCode and land in localStorage.
      const lang = this.normalizeLanguageCode(l);
      this.selectedLanguage = lang;
      localStorage.setItem("language_code", lang);
    });

    // Fetch route parameters and remote data if applicable
    this.getActivatedRoute();

    // Get preferred currency from user details
    this.authService.userDetails$.pipe(
      take(1),
      tap((user: Data) => {
        this.preferredCurrency = user?.preferred_currency ?? CommonVariable.preferredCurrency;
      }),
      switchMap(() => this.authService.getCurrencyList$.pipe(take(1)))
    )
      .subscribe((data: Data) => {
        const match = data?.details?.find((x: Data) => x?.currency_code === this.preferredCurrency);
        this.currencySymbol = match?.currency_symbol || "£";
      });
  }

  async ngOnInit(): Promise<void> {

    await this.authService
      ?.getUid("FreeRegStep4JComponent")
      ?.then((uid) => {
        this.userId = uid;
        this.analytics.logEvent("user_details_screen", {
          userId: this.userId,
        });
      })
      .catch((error: any) => {
        return error;
      });

    const storedLanguage = localStorage.getItem("language_code") || "en";
    this.initializeLanguage(storedLanguage);
      }

  ngAfterViewInit(): void {
    // Apply the already-selected language as the default if currentLanguage is not
    // yet set, so the form reflects the current app language rather than always
    // falling back to "en" when no valid userDetails$ emission has arrived.
    if (!this.accountDetails?.get('currentLanguage')?.value) {
      // Use normalizeLanguageCode so the fallback is always a valid, lower-case,
      // supported language code — not a raw value from localStorage or selectedLanguage
      // that may be stale, empty, or differently cased.
      const fallbackLang = this.normalizeLanguageCode(
        this.selectedLanguage ||
        localStorage.getItem("language_code") ||
        CommonVariable.preferredLanguage
      );
      this.accountDetails?.patchValue({ currentLanguage: fallbackLang });
    }

    // Override with the user's profile language once a confirmed valid emission
    // arrives.  language_code is normalized once (trim + toLowerCase) so casing
    // or whitespace differences in the service response never silently reject a
    // valid code or patch an inconsistent value into the form.
    //
    // Type guard: `filter((data): data is NonNullable<typeof data> => ...)`
    //   Rejects null/undefined and objects where language_code is absent or
    //   not in CommonVariable.allLanguageCodes after normalization.
    //   Note: email is intentionally NOT required here — phone-only/social
    //   accounts may not have email populated, so checking it would leave those
    //   users permanently on the fallback language.
    //
    // take(1) — INTENTIONAL: this subscription is for initial pre-fill only.
    //   Completing after the first genuine emission ensures that later userDetails$
    //   updates (e.g. token refresh) do not silently overwrite any language the user
    //   may have changed in the form.  If user details are later corrected with a
    //   different language code, a page reload would pick up the new value.
    //
    // takeUntilDestroyed cancels the subscription if the component is destroyed
    // before any valid emission arrives, preventing patchValue on a destroyed form.
    this.authService.userDetails$
      .pipe(
        filter((data): data is NonNullable<typeof data> => {
          if (data == null) return false;
          // typeof guard: malformed API data could provide a truthy non-string value;
          // calling .trim() on a non-string would throw at runtime.
          if (typeof data.language_code !== 'string' || !data.language_code) return false;
          const code = data.language_code.trim().toLowerCase();
          return !!code && CommonVariable.allLanguageCodes.includes(code as Language);
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        // Only patch if the user has not manually changed the field (dirty check),
        // preventing this initial pre-fill from overwriting an in-progress user edit
        // that occurred while userDetails$ was still loading.
        if (this.accountDetails?.get('currentLanguage')?.dirty) return;
        const code = this.normalizeLanguageCode(data.language_code);
        this.accountDetails?.patchValue({ currentLanguage: code });
      });
  }

  /**
   * Normalizes a language code string (trim + lowercase) and validates it against
   * the supported list.  Returns the normalized code if valid, or the app-level
   * preferred language as a safe fallback.  Use this everywhere a language value
   * is persisted or applied to prevent invalid/mixed-case values entering state.
   */
  private normalizeLanguageCode(lang: string | null | undefined): string {
    const normalized = (lang ?? "").trim().toLowerCase();
    if (CommonVariable.allLanguageCodes.includes(normalized as Language)) return normalized;
    // Normalize the fallback too — preferredLanguage is typically already lower-case
    // but guard against future changes to the constant or casing inconsistencies.
    const fallback = (CommonVariable.preferredLanguage ?? "en").trim().toLowerCase();
    return CommonVariable.allLanguageCodes.includes(fallback as Language) ? fallback : "en";
  }

  // Initialize language settings.
  // translate.use() and local state (this.selectedLanguage, localStorage) are
  // updated synchronously so any code that runs immediately after this call
  // always observes the correct current language.
  //
  // The shared selectedlanguage$ BehaviorSubject is updated asynchronously via
  // languageEmit$ + timer(0) (see constructor).  This deferral is deliberate:
  // emitting synchronously would fire all BehaviorSubject subscribers in the same
  // call frame; on WebKit/iPhone the combined depth (ngOnInit → initializeLanguage
  // → subscribers → Angular CD) exceeds the shallower JS call-stack limit, causing
  // "RangeError: Maximum call stack size exceeded".
  // partial-registration uses the same setTimeout(0) pattern for the same reason.
  //
  // If initializeLanguage() is called multiple times rapidly, switchMap in the
  // constructor pipeline cancels previous pending timers so only the latest
  // selectedlanguage$.next() fires; translate.use() and local state still update
  // on every call.
  private initializeLanguage(language: string) {
    const lang = this.normalizeLanguageCode(language);
    this.translate.use(lang);
    // Update local state synchronously so any synchronous callers see the new value.
    this.selectedLanguage = lang;
    localStorage.setItem("language_code", lang);
    // Defer only the shared BehaviorSubject emission to avoid stack overflow on WebKit.
    this.languageEmit$.next(lang);
  }

  // Parse route parameters and load relevant data
  getActivatedRoute() {
    this.actRoute.queryParamMap
      .pipe(
        map((params: Data) => {
          const result: Data = {};
          params.keys.forEach((key) => (result[key] = params.get(key)));
          this.navParams = result;
          return result;
        }),


        // Prefer CF paymentHtml (currency DVs already resolved), same as payment.page / 4c.
        // If prosub is missing or partial_Reg fails / has no paymentHtml, fall back to the
        // previous route-param + getPaymentPageData path and resolve DVs via getDVFromString.

        // Single pipe (no nested subscribe) so route / partial_Reg / fallback stay cancelable.
        switchMap((params) => {
          const prosubId = params?.prosub_id || params?.Prosub_ID;
          if (!prosubId) {
            return this.bindRouteParamFallback$(params);
          }

          return this.authPartialRegService.partial_Reg(prosubId, "", "").pipe(
            catchError(() => of(null)),
            switchMap((s) => {
              if (s && !s.error && s.paymentHtml) {
                this.bindPartialRegSuccess(params, s);
                return EMPTY;
              }
              return this.bindRouteParamFallback$(params, prosubId);
            })
          );
        }),

        catchError(() => {
          this.finishOptMsgLoad();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

  }


  private bindPartialRegSuccess(params: Data, s: Data): void {
    this.applyJackpotPlaceholder(s.paymentHtml || "");

    this.routerData = { ...params, ...(s.prosubDetail || {}) };
    this.priceValue = s.prosubDetail?.int_price?.[this.preferredCurrency] ?? 0;
    this.offerName = s.prosubDetail?.offer_name ?? "";
    this.finishOptMsgLoad();
  }

  /** Previous 4j path: merge Firestore payment page data with route params, then resolve DVs. */
  private bindRouteParamFallback$(params: Data, prosubId?: string): Observable<string> {
    const id = prosubId || params?.prosub_id || params?.Prosub_ID;
    const source$ = id
      ? this.paymentDataService.getPaymentPageData(id).pipe(
        take(1),
          map((res) => ({ ...params, ...res })),
          catchError(() => of(params))
        )
      : of(params);

     return source$.pipe(
      switchMap((finalParams) => {
        if (!finalParams) {
          this.finishOptMsgLoad();
          return EMPTY;
        }

      this.routerData = finalParams;
      this.priceValue = finalParams?.int_price?.[this.preferredCurrency] ?? 0;
      this.offerName = finalParams?.offer_name ?? "";

      const rawHtml = finalParams?.opt_msg || "";
      if (!rawHtml) {
        this.optTemplateHtml = "";
        this.finishOptMsgLoad();
        return EMPTY;
      }
      return from(this.authService.getDVFromString(rawHtml)).pipe(
        map((resolved: string) => resolved || rawHtml),
        catchError(() => of(rawHtml)),
        tap((html: string) => {
          this.applyJackpotPlaceholder(html);
          this.finishOptMsgLoad();
        })
      );
    })
  );
}

  private applyJackpotPlaceholder(html: string): void {
    const jackpotValue = `${this.currencySymbol}45M`;
    this.optTemplateHtml = html.includes("[su_standalone_euro_jackpot]")
      ? html.replace(/\[su_standalone_euro_jackpot\]/g, jackpotValue)
      : html;
  }

  private finishOptMsgLoad(): void {
    this.loadingService.isLodingSignal.set(true);
    this.loadingService.dismiss();
  }  

  onSortCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const temporaryValue = input.value;
    const selectionStart = input.selectionStart ?? temporaryValue.length;
    const digitCaret = this.countSortCodeDigits(temporaryValue.slice(0, selectionStart));
    this.updateSortCodeState(input, temporaryValue, digitCaret);
  }

  onSortCodeKeydown(event: KeyboardEvent): void {
    if (event.key !== "Backspace") return;
    const input = event.target as HTMLInputElement;
    if (input.selectionStart !== input.selectionEnd) return;

    const caret = input.selectionStart ?? 0;
    const value = input.value;
    if (caret > 0 && value[caret - 1] === "-") {
      event.preventDefault();
      const digitsBeforeCaret = this.countSortCodeDigits(value.slice(0, caret));
      const raw = this.sortCodeDigitsOnly(value);
      const removeIndex = Math.max(0, digitsBeforeCaret - 1);
      const nextRaw = raw.slice(0, removeIndex) + raw.slice(removeIndex + 1);
      this.updateSortCodeState(input, nextRaw, removeIndex);
    }
  }

  private sortCodeSixDigitValidator = (control: AbstractControl) => {
    const digits = this.sortCodeDigitsOnly(control.value);
    if (!control.value) return null;
    return digits.length === 6 ? null : { pattern: true };
  };

  private sortCodeDigitsOnly(value: string): string {
    return String(value || "").replace(/\D/g, "").slice(0, 6);
  }

  private formatSortCode(rawDigits: string): string {
    const digits = this.sortCodeDigitsOnly(rawDigits);
    const first = digits.slice(0, 2);
    const second = digits.slice(2, 4);
    const third = digits.slice(4, 6);
    if (digits.length <= 1) return first;
    if (digits.length === 2) return `${first}-`;
    if (digits.length === 3) return `${first}-${second}`;
    if (digits.length === 4) return `${first}-${second}-`;
    return `${first}-${second}-${third}`;
  }

  private countSortCodeDigits(value: string): number {
    const matches = String(value || "").match(/\d/g);
    return matches ? matches.length : 0;
  }

  private caretAfterSortCodeDigitCount(formattedValue: string, digitCount: number): number {
    if (digitCount <= 0) return 0;
    let seen = 0;
    for (let index = 0; index < formattedValue.length; index += 1) {
      if (/\d/.test(formattedValue[index])) {
        seen += 1;
      }
      if (seen === digitCount) {
        let caret = index + 1;
        while (formattedValue[caret] === "-") {
          caret += 1;
        }
        return caret;
      }
    }
    return formattedValue.length;
  }

  private updateSortCodeState(input: HTMLInputElement, rawDigits: string, requestedDigitCaret?: number): void {
    const raw = this.sortCodeDigitsOnly(rawDigits);
    const formatted = this.formatSortCode(raw);
    this.accountDetails.get("sort_code")?.setValue(formatted, { emitEvent: false });
    input.value = formatted;
    if (typeof requestedDigitCaret === "number" && document.activeElement === input) {
      const caret = this.caretAfterSortCodeDigitCount(formatted, Math.min(requestedDigitCaret, raw.length));
      requestAnimationFrame(() => input.setSelectionRange(caret, caret));
    }
  }

  // Form submission handler
  onSubmit() {
    const payloadData = { ...this.accountDetails.value, ...this.routerData, amount:this.priceValue};
    payloadData.sort_code = this.sortCodeDigitsOnly(payloadData.sort_code);
    this.paymentService.insertCallCenterDirectDebitDetails(payloadData).subscribe((res) => {
      const { result, directDebitStatus, status, errorMessage } = res;
      this.progressBar = 100;
      if (result?.Valid === "Valid" && directDebitStatus === "success") {
        setTimeout(() => {
          this.spinnerAnimation = false;
          this.router.navigate(["/free-reg-complete-profile"], {
            queryParams: { ...this.navParams },
          });
        }, 800)
      }

      if (status === "error") {
        const message = {
          message: { line_1: errorMessage, line_2: "" },
          style: { icon: "tick circle error.svg", type: "Error" },
        };
        this.spinnerAnimation = false;
        this.authService.showToastMessage(message, message);
        return;
      }
    });
  }

  // Fetch address list based on post code
  lookupAddress() {
    const postcode = this.accountDetails.get("post_code")?.value;

    if (!postcode) {
      const message = {
        message: { line_1: "Post code is required", line_2: "" },
        style: { icon: "tick circle error.svg", type: "Error" },
      };
      this.authService.showToastMessage(message, message);
      return;
    }

    this.paymentService.getpostcodeaddresslookupDetails(postcode).subscribe((addresses: Data) => {
      this.addressList = addresses?.data;
      this.openAddressSelectModal();
    });
  }

  // Open address selection modal
  async openAddressSelectModal() {
    const alert = await this.alertController.create({
      header: "Select Address",
      inputs: this.addressList.map((addr, index) => ({
        type: "radio",
        label: addr.textContent,
        value: index.toString(),
      })),
      buttons: [
        { text: "Cancel", role: "cancel" },
        {
          text: "OK",
          handler: (selectedIndex: string) => {
            const selectedAddress = this.addressList[+selectedIndex];
            this.fillAddress(selectedAddress);
          },
        },
      ],
      cssClass: "address-select-modal",
    });

    await alert.present();
  }

  startSpinner() {
    this.spinnerAnimation = true;
    this.progressBar = 0;
    this.timer$ = interval(200);
    this.sub = this.timer$?.pipe()?.subscribe((sec) => {
      this.progressBar = 0 + sec;
    });
  }

  // Fill form fields with selected address
  fillAddress(address: Data) {
    this.accountDetails.patchValue({
      address_1: address.address_1,
      address_2: address.address_2,
      town: address.town,
      post_code: address.postcode,
    });
  }

  


  openNoThanks() {
  this.matDialog.open(NoThanksPopupComponent, {
      height: "40%",
      width: "80%",

    });
  }
}